/**
 * @file GlobalRagProgressPanel.jsx
 * @description Navigation-aware wrapper for RagProgressPanel.
 *
 * Consumes RagJobContext and renders ONE progress bar per active master job:
 * - A presa launches TWO masters at once (base + `(Prioritario)`) → two bars.
 * - A normal collection/agent sync launches exactly ONE master → one bar,
 *   identical to the previous single-panel behaviour.
 * Bars are stacked in a fixed bottom-right column. Each bar owns its own
 * auto-dismiss timer and per-master cancel/close.
 *
 * - On knowledge pages (/collections, /editor/knowledge/*): expanded by default
 * - On other pages: collapsed mini-bar, expandable on click
 * - Terminal states (completed/cancelled): auto-dismiss after 8s
 * - Failed state: persists until user dismisses
 *
 * @module components/rag/GlobalRagProgressPanel
 */

import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useRagJob } from '../../context/RagJobContext';
import RagProgressPanel from './RagProgressPanel';

const AUTO_DISMISS_MS = 8000;

/**
 * A single master's progress bar. Owns its own auto-dismiss timer so each master
 * dismisses independently (one presa collection can finish before the other).
 */
const RagJobBar = ({ job, isKnowledgePage, folderProgressDetail, cancelJob, cancelling, stopTracking }) => {
  const dismissTimerRef = useRef(null);

  const status = job?.status;
  const isTerminal = status === 'completed' || status === 'completed_with_errors' || status === 'cancelled' || status === 'credit_exhausted';
  const isFailed = status === 'failed' || status === 'dispatch_failed';

  // folderProgressDetail is global (any rol); only relevant if it belongs to THIS master's rol.
  const liveProgressDetail = folderProgressDetail?.rolId === job?.rol_id ? folderProgressDetail : null;

  // Auto-dismiss on terminal states (not failed)
  useEffect(() => {
    if (isTerminal && !isFailed) {
      dismissTimerRef.current = setTimeout(() => {
        stopTracking(job.id);
      }, AUTO_DISMISS_MS);

      return () => {
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = null;
        }
      };
    }
  }, [isTerminal, isFailed, stopTracking, job.id]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  return (
    <RagProgressPanel
      activeJob={job}
      onCancel={cancelJob}
      cancelling={cancelling}
      onClose={() => stopTracking(job.id)}
      defaultExpanded={isKnowledgePage}
      liveProgressDetail={liveProgressDetail}
      // Override the panel's own fixed positioning: the wrapper Box owns the
      // fixed bottom-right anchor and stacks the bars in a column.
      sx={{ position: 'relative', bottom: 'auto', right: 'auto', width: '100%', maxHeight: 'none', zIndex: 'auto' }}
    />
  );
};

const GlobalRagProgressPanel = () => {
  const { activeJobs, activeJob, cancelJob, cancelling, stopTracking, folderProgressDetail } = useRagJob();
  const location = useLocation();

  // Canonical source is the array; fall back to the singular alias so any
  // consumer/test that only provides `activeJob` still renders one bar.
  const jobs = (Array.isArray(activeJobs) && activeJobs.length > 0)
    ? activeJobs
    : (activeJob ? [activeJob] : []);

  const isKnowledgePage =
    location.pathname === '/collections' ||
    location.pathname.startsWith('/editor/knowledge/');

  if (jobs.length === 0) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1300,
        width: 400,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      {jobs.map((job) => (
        <RagJobBar
          key={job.id}
          job={job}
          isKnowledgePage={isKnowledgePage}
          folderProgressDetail={folderProgressDetail}
          cancelJob={cancelJob}
          cancelling={cancelling}
          stopTracking={stopTracking}
        />
      ))}
    </Box>
  );
};

export default GlobalRagProgressPanel;
